// ===== 1. Imports =====
import React, { useEffect, useState } from "react";
import Header from "./components/Header";
import DashboardPage from "./pages/DashboardPage/index";
import SuppliersPage from "./pages/SuppliersPage";
import BudgetPage from "./pages/BudgetPage";
import UsersPage from "./pages/UsersPage";
import ReportsPage from "./pages/ReportsPage";
import ProjectsPage from "./pages/ProjectsPage";
import LoginPage from "./pages/LoginPage";
import RevisionPage from "./pages/RevisionPage";
import PaymentsPage from "./pages/PaymentsPage";
import LoadingScreen from "./components/LoadingScreen";
import { db, auth } from "./firebaseConfig";
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

// ===== 2. Componente Principal =====
function App() {
  // ===== 2.1 Estados Principales =====
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("DASHBOARD");
  const [projectName, setProjectName] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [activeProject, setActiveProject] = useState(null);

  // ===== 2.2 Datos =====
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [departments, setDepartments] = useState([]);

  // ===== 3. Restaurar Proyecto Activo =====
  useEffect(() => {
    const savedProject = localStorage.getItem("activeProject");
    if (savedProject) setActiveProject(JSON.parse(savedProject));
  }, []);

  useEffect(() => {
    if (activeProject)
      localStorage.setItem("activeProject", JSON.stringify(activeProject));
  }, [activeProject]);

  // ===== 4. Login =====
  const handleLogin = async ({ project, email, password }) => {
    try {
      setIsLoading(true);

      // --- Acceso SUPERADMIN ---
      if (
        project === "SUPERADMIN" &&
        email === "root@filmaapp.es" &&
        password === "SUPERADMIN"
      ) {
        await signInWithEmailAndPassword(auth, email, password);

        const session = {
          projectName: "Superadmin",
          projectCode: "SUPERADMIN",
          userName: "Superadmin",
          userRole: "superadmin",
          isSuperadmin: true,
          activeTab: "PROYECTOS",
        };
        sessionStorage.setItem("filma_session", JSON.stringify(session));

        setProjectName("Superadmin");
        setProjectCode("SUPERADMIN");
        setUserName("Superadmin");
        setUserRole("superadmin");
        setIsSuperadmin(true);

        setTimeout(() => {
          setIsLoading(false);
          setIsLoggedIn(true);
          setActiveTab("PROYECTOS");
        }, 600);
        return;
      }

      // --- Acceso normal ---
      await signInWithEmailAndPassword(auth, email, password);

      const q = query(collection(db, "projects"), where("code", "==", project));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert("Proyecto no encontrado en Firestore");
        setIsLoading(false);
        return;
      }

      const projectFound = snapshot.docs[0].data();
      const userSnap = await getDoc(
        doc(db, "projects", project, "users", email)
      );

      if (!userSnap.exists()) {
        alert("Usuario no encontrado en el proyecto.");
        setIsLoading(false);
        return;
      }

      const userData = userSnap.data();

      const session = {
        projectName: projectFound.name,
        projectCode: projectFound.code,
        userName: userData.name,
        userRole: userData.role,
        isSuperadmin: false,
        activeTab: "DASHBOARD",
      };

      sessionStorage.setItem("filma_session", JSON.stringify(session));
      setProjectName(session.projectName);
      setProjectCode(session.projectCode);
      setUserName(session.userName);
      setUserRole(session.userRole);
      setIsSuperadmin(false);
      setActiveProject(projectFound);

      setTimeout(() => {
        setIsLoading(false);
        setIsLoggedIn(true);
        setActiveTab("DASHBOARD");
      }, 800);
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      setIsLoading(false);
      throw new Error("Credenciales incorrectas o error de conexión.");
    }
  };

  // ===== 5. Logout =====
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Error al cerrar sesión:", e);
    }

    // --- Reset global ---
    setIsLoggedIn(false);
    setActiveTab("DASHBOARD");
    setUserRole("");
    setUserName("");
    setProjectName("");
    setProjectCode("");
    setIsSuperadmin(false);
    setActiveProject(null);
    sessionStorage.removeItem("filma_session");
    localStorage.removeItem("activeProject");
  };

  // ===== 6. Restaurar Sesión =====
  useEffect(() => {
    const savedSession = sessionStorage.getItem("filma_session");
    if (savedSession) {
      const {
        projectName,
        projectCode,
        userName,
        userRole,
        activeTab,
        isSuperadmin: isSA,
      } = JSON.parse(savedSession);

      setProjectName(projectName);
      setProjectCode(projectCode);
      setUserName(userName);
      setUserRole(userRole);
      setIsSuperadmin(!!isSA);
      setActiveTab(activeTab || "DASHBOARD");
      setIsLoggedIn(true);
    }

    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) setIsLoggedIn(false);
    });
    return () => unsub();
  }, []);

  // ===== 7. Firestore Listeners =====
  useEffect(() => {
    if (!projectCode || userRole === "superadmin") return;

    const unsubSuppliers = onSnapshot(
      collection(db, "projects", projectCode, "suppliers"),
      (snap) => setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubPos = onSnapshot(
      collection(db, "projects", projectCode, "pos"),
      (snap) => setPos(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubUsers = onSnapshot(
      collection(db, "projects", projectCode, "users"),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubDepartments = onSnapshot(
      collection(db, "projects", projectCode, "departments"),
      (snap) =>
        setDepartments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    // 🔄 CAMBIO: Escuchar colección "budget" (no "accounts")
    const unsubAccounts = onSnapshot(
      collection(db, "projects", projectCode, "budget"),
      (snap) => setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubSuppliers();
      unsubPos();
      unsubUsers();
      unsubDepartments();
      unsubAccounts();
    };
  }, [projectCode, userRole]);

  // ===== 8. Restricciones por Rol =====
  useEffect(() => {
    const restrictedTabs = {
      PM: [],
      Controller: [],
      HOD: ["PRESUPUESTO", "USUARIOS", "INFORMES"],
      Coordinator: ["PRESUPUESTO", "USUARIOS", "INFORMES"],
      Crew: ["PRESUPUESTO", "USUARIOS", "INFORMES"],
    };

    if (restrictedTabs[userRole]?.includes(activeTab)) {
      setActiveTab("DASHBOARD");
    }
  }, [userRole, activeTab]);

  // ===== 9. Render =====
  if (isLoading) {
    const fallbackSession = JSON.parse(
      sessionStorage.getItem("filma_session") || "{}"
    );
    const name =
      projectName ||
      fallbackSession.projectName ||
      fallbackSession.projectCode ||
      "Cargando proyecto...";
    return <LoadingScreen projectName={name} />;
  }
  if (!isLoggedIn) return <LoginPage onLogin={handleLogin} />;

  return (
    <div className="app-shell app-shell--dashboard">
      <Header
        projectName={projectName}
        userName={userName}
        userRole={userRole}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleLogout={handleLogout}
      />

      {/* --- Panel de Proyectos (Superadmin) --- */}
      {userRole === "superadmin" && activeTab === "PROYECTOS" && (
        <ProjectsPage
          onEnterProject={(project, role, department) => {
            const session = {
              projectName: project.name,
              projectCode: project.code,
              userName: "Superadmin",
              userRole: role,
              isSuperadmin: true,
              activeTab: "DASHBOARD",
              department,
            };
            sessionStorage.setItem("filma_session", JSON.stringify(session));
            setProjectName(project.name);
            setProjectCode(project.code);
            setUserName("Superadmin");
            setUserRole(role);
            setIsSuperadmin(true);
            setActiveProject(project);
            setActiveTab("DASHBOARD");
          }}
        />
      )}

      {/* --- Dashboard modular --- */}
      {activeTab === "DASHBOARD" && (
        <DashboardPage
          projectCode={activeProject?.code || projectCode}
          suppliers={suppliers}
          departments={departments}
          users={users}
          accounts={accounts}
          userRole={userRole}
          userName={userName}
          department={activeProject?.department}
          setActiveTab={setActiveTab}
        />
      )}

      {/* --- Proveedores --- */}
      {activeTab === "PROVEEDORES" && (
        <SuppliersPage
          projectCode={projectCode}
          suppliers={suppliers}
          setSuppliers={setSuppliers}
          pos={pos}
        />
      )}

      {/* --- Presupuesto --- */}
      {activeTab === "PRESUPUESTO" &&
        ["PM", "Controller", "superadmin"].includes(userRole) && (
          <BudgetPage
            projectCode={projectCode}
            accounts={accounts}
            setAccounts={setAccounts}
            pos={pos}
          />
        )}

      {/* --- Usuarios --- */}
      {activeTab === "USUARIOS" &&
        ["PM", "Controller", "superadmin"].includes(userRole) && (
          <UsersPage
            projectCode={projectCode}
            admins={admins}
            setAdmins={setAdmins}
            departments={departments}
            setDepartments={setDepartments}
            users={users}
            setUsers={setUsers}
            userRole={userRole}
            isSuperadmin={isSuperadmin}
          />
        )}

      {/* --- Informes --- */}
      {activeTab === "INFORMES" &&
        ["PM", "Controller", "superadmin"].includes(userRole) && (
          <ReportsPage />
        )}

      {activeTab === "REVISION" &&
        ["PM", "Controller", "HOD"].includes(userRole) && (
          <RevisionPage projectCode={projectCode} userRole={userRole} />
        )}

      {activeTab === "PAGOS" && userRole === "Controller" && (
        <PaymentsPage projectCode={projectCode} userRole={userRole} />
      )}
    </div>
  );
}

// ===== 10. Export =====
export default App;
